import { apiFetch } from '@/lib/api';
import { uploadAttachment } from '@/lib/uploadAttachment';
import { useOrgStore } from '@/stores/orgStore';
import { GalleryImage } from '@/types/attachment';
import { storageKey } from '@/config/app';
import { invalidateAttachmentCache } from '@/components/roros/useAttachmentThumbnail';

export class AttachmentsService {
  private orgHeaders(): Record<string, string> {
    try {
      const stateOrgId = useOrgStore.getState().activeOrgId;
      const storedOrgId = localStorage.getItem(storageKey('active_org_id'));
      const orgId = stateOrgId || storedOrgId || undefined;
      return orgId ? { 'X-Organization-Id': orgId } : {};
    } catch {
      return {};
    }
  }

  async list(q?: string): Promise<GalleryImage[]> {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    const res = await apiFetch(`/api/attachments?${params.toString()}`, { headers: this.orgHeaders() });
    if (!res.ok) {
      if (res.status === 503) {
        throw new Error('File storage is not configured');
      }
      return [];
    }
    const payload = await res.json();
    const files = payload?.data || payload?.items || [];
    return files.map((f: any) => ({
      id: f.id,
      filename: f.filename || f.originalName || f.title || f.stored_filename,
      originalName: f.originalName || f.original_filename || f.title || '',
      // UI sometimes expects originalFilename and thumbnailUrl
      originalFilename: f.originalName || f.original_filename || f.title || '',
      thumbnailUrl: f.thumbnail || f.thumbnail_url || undefined,
      url: f.url,
      thumbnail: f.thumbnail || f.thumbnail_url || undefined,
      title: f.title,
      metadata: {
        uploadedAt: new Date(f.metadata?.uploadedAt || f.created_at || Date.now()).toISOString(),
        size: f.metadata?.size ?? f.size ?? 0,
        width: f.metadata?.width ?? f.width ?? null,
        height: f.metadata?.height ?? f.height ?? null,
        tags: f.metadata?.tags ?? f.tags ?? [],
        mimeType: f.mime_type || f.metadata?.mimeType,
      },
      description: f.description || f.ai_summary || null,
      aiAnalyzedAt: f.aiAnalyzedAt || f.ai_analyzed_at ? new Date(f.aiAnalyzedAt || f.ai_analyzed_at).toISOString() : null,
    }));
  }

  async upload(file: File, options: { title?: string; tags?: string[] } = {}): Promise<GalleryImage> {
    // Shared presign → PUT → finalize pipeline (see src/lib/uploadAttachment.ts).
    // finalizeData carries the full attachment record returned by /api/attachments/finalize,
    // so we don't need a second HTTP round-trip to fetch it.
    const { finalizeData } = await uploadAttachment(file, {
      title: options.title,
      tags: options.tags,
    });

    invalidateAttachmentCache();
    const result = finalizeData as { data?: unknown } | unknown;
    return this.mapResponseToAttachment(
      (result as { data?: unknown })?.data ?? result,
    );
  }

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`/api/attachments/${id}`, {
      method: 'DELETE',
      headers: this.orgHeaders(),
    });
    if (!res.ok) throw new Error('Failed to delete attachment');
    invalidateAttachmentCache();
  }

  async rename(id: string, title: string): Promise<void> {
    const res = await apiFetch(`/api/attachments/${id}`, {
      method: 'PATCH',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    if (!res.ok) throw new Error('Failed to rename attachment');
    invalidateAttachmentCache();
  }

  async update(id: string, data: { title?: string; description?: string; tags?: string[] }): Promise<void> {
    const payload: Record<string, any> = {};
    if (data.title !== undefined) payload.title = data.title;
    if (data.description !== undefined) payload.description = data.description;
    if (data.tags !== undefined) payload.tags = data.tags;

    const res = await apiFetch(`/api/attachments/${id}`, {
      method: 'PATCH',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to update attachment');
    invalidateAttachmentCache();
  }

  async getById(id: string): Promise<GalleryImage | null> {
    const res = await apiFetch(`/api/attachments/${id}`, { headers: this.orgHeaders() });
    if (!res.ok) return null;
    const payload = await res.json();
    const f = payload?.data;
    if (!f) return null;
    return this.mapResponseToAttachment(f);
  }

  async analyze(id: string): Promise<void> {
    const res = await apiFetch(`/api/attachments/${id}/analyze`, {
      method: 'POST',
      headers: this.orgHeaders(),
    });
    if (!res.ok) throw new Error('Failed to start attachment analysis');
  }

  private mapResponseToAttachment(f: any): GalleryImage {
    return {
      id: f.id,
      filename: f.filename || f.originalName || f.title || f.stored_filename,
      originalName: f.originalName || f.original_filename || f.title || '',
      url: f.url,
      thumbnail: f.thumbnail || f.thumbnail_url || f.url,
      title: f.title,
      metadata: {
        uploadedAt: new Date(f.metadata?.uploadedAt || f.created_at || Date.now()).toISOString(),
        size: f.metadata?.size ?? f.size ?? 0,
        width: f.metadata?.width ?? f.width ?? null,
        height: f.metadata?.height ?? f.height ?? null,
        tags: f.metadata?.tags ?? f.tags ?? [],
        mimeType: f.mime_type || f.metadata?.mimeType,
      },
      description: f.description || f.ai_summary || null,
      aiAnalyzedAt: f.aiAnalyzedAt || f.ai_analyzed_at ? new Date(f.aiAnalyzedAt || f.ai_analyzed_at).toISOString() : null,
    };
  }
}


